"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="234007" className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="animate-pulse">
            <div className="bg-muted aspect-[16/9] rounded-none mb-6"></div>
            <div className="flex gap-4 mb-4">
              <div className="h-6 bg-muted w-20 rounded-none"></div>
              <div className="h-6 bg-muted w-24 rounded-none"></div>
            </div>
            <div className="h-8 bg-muted w-full mb-2 rounded-none"></div>
            <div className="h-8 bg-muted w-2/3 rounded-none"></div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 border border-border rounded-none bg-card/20">
        <p className="text-muted-foreground mb-4 font-default">Failed to load podcast episodes.</p>
        <button onClick={refetch} className="text-primary underline font-medium font-default">
          Try Again
        </button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-12 border border-border rounded-none bg-card/20">
        <p className="text-muted-foreground font-default">No episodes recorded yet. Stay tuned.</p>
      </div>;
  }
  const fallbackImages = ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1644405/img/industrial_facility_conduit.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1644405/img/electrical_cable_spools.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1644405/img/commercial_solar_installation.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1644405/img/warehouse_logistics_latam.png"];
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16">
      {posts.map((post, i) => {
      const category = post.categories?.[0]?.name || "Podcast";
      const date = post.date ? new Date(post.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }) : "Latest";
      const imageSrc = post.featuredImage || fallbackImages[i % fallbackImages.length];
      return <article key={post.id} data-index={i} className="group h-full">
            <Link to={post.link || "#"} className="block h-full">
              <Card className="h-full border border-border/50 shadow-none bg-transparent rounded-none flex flex-col gap-0 transition-colors duration-300 hover:bg-white/5">
                <div className="relative overflow-hidden aspect-[16/9] mb-6 rounded-none bg-muted">
                  <img src={imageSrc} alt={post.title} className="object-cover w-full h-full transition-transform duration-700 ease-out group-hover:scale-105" />
                  {}
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-primary/40 transition-colors duration-300 flex items-center justify-center">
                    <div className="flex items-center justify-center w-16 h-16 rounded-full bg-white/20 backdrop-blur-md border border-white/30 opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300 ease-out animate-pulse">
                      <ArrowRight className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>
                
                <CardContent className="p-6 flex flex-col grow">
                  <div className="flex items-center gap-4 mb-4">
                    <Badge variant="secondary" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-2.5 py-0.5 font-mono text-xs uppercase tracking-widest">
                      {category}
                    </Badge>
                    <time className="font-mono text-xs text-muted-foreground uppercase tracking-widest">
                      {date}
                    </time>
                  </div>
                  
                  <h3 className="text-xl font-bold font-default text-foreground leading-tight group-hover:text-chart-2 transition-colors duration-300 line-clamp-3">
                    {post.title}
                  </h3>
                  
                  {post.excerpt && <div className="mt-4 text-muted-foreground line-clamp-2 text-sm font-default" dangerouslySetInnerHTML={{
                __html: post.excerpt
              }} />}
                </CardContent>
              </Card>
            </Link>
          </article>;
    })}
    </div>;
}
export default function IndustryIntelligence() {
  const wp_query_params = {
    post_type: "post",
    per_page: 4,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="cancast-with-the-cancrew" className="dark bg-background py-20 md:py-32 border-t border-border/10">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="mb-16 md:mb-24 max-w-3xl">
          <h2 className="text-4xl md:text-6xl font-bold font-default text-foreground tracking-tighter mb-6 uppercase">Recent Posts</h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-default">
            Deep dives into civic data, governance trends, and interviews with the advocates shaping the future of responsive government.
          </p>
        </div>

        <PostsProvider className="posts-grid-wrapper" wp_query={{
        post_type: "post",
        per_page: 4,
        order: "desc",
        orderby: "date"
      }}>
          <PostsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}